import type { Profil } from '../lib/types';
import type { CompteursInscriptions } from '../lib/useApprenants';
import type { NomIcone } from './icons';

export interface IndicateurVue {
  libelle: string;
  valeur: string | number;
  detail: string;
  ton?: string;
}

export interface ActionSecondaire {
  libelle: string;
  icone: NomIcone;
  to: string;
}

export interface BandeauBasConfig {
  titre: string;
  badgeTexte: (compteurs?: CompteursInscriptions, effectifs?: { effectif: (s: string) => number }) => string;
  badgeTon: 'danger' | 'warning' | 'info' | 'mint';
  description: string;
  boutonAction?: {
    libelle: string;
    statutFiltre: string;
  };
  liensSecondaires?: {
    to: string;
    libelle: string;
    mode: 'bouton' | 'lien';
  }[];
}

export interface LearnersViewConfig {
  profil: Profil;
  badgePole: string;
  titre: string;
  sousTitre: (effectifs: { portefeuille: number; enFormation: number }) => string;
  actionsSecondaires: ActionSecondaire[];
  indicateurs: (data: {
    portefeuille: number;
    compteurs?: CompteursInscriptions;
    effectif: (statut: string) => number;
  }) => IndicateurVue[];
  colonnes: {
    cle: string;
    libelle: string;
  }[];
  statutsPrioritaires?: string[];
  masquerFinanceurSensible?: boolean;
  bandeauBas: BandeauBasConfig;
}

/**
 * Configuration par pôle / profil de l'écran « /learners ».
 * La vue administrative sert de référence visuelle et fonctionnelle commune.
 */
export function getLearnersViewConfig(profil: Profil | undefined | null): LearnersViewConfig {
  switch (profil) {
    case 'RECRUTEMENT':
      return {
        profil: 'RECRUTEMENT',
        badgePole: 'Pôle Recrutement',
        titre: 'Candidats & Recrutement',
        sousTitre: ({ portefeuille }) =>
          `${portefeuille} candidature${portefeuille > 1 ? 's' : ''} & dossier${
            portefeuille > 1 ? 's' : ''
          } en cours d’instruction`,
        actionsSecondaires: [
          { libelle: 'Planning des entretiens', icone: 'calendrier', to: '/com-entretiens' },
          { libelle: 'Relances candidats', icone: 'message', to: '/com-relances' },
        ],
        indicateurs: ({ portefeuille, compteurs, effectif }) => [
          { libelle: 'Candidats suivis', valeur: portefeuille, detail: 'vivier actif' },
          {
            libelle: 'Candidatures du mois',
            valeur: compteurs?.inscriptionsDuMois ?? '—',
            detail: 'nouvelles entrées',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'Entretiens à mener',
            valeur: compteurs?.entretiensAMener ?? '—',
            detail: 'inscrits & planifiés',
            ton: 'text-codialis-navy',
          },
          {
            libelle: 'Pièces en attente',
            valeur: compteurs?.dossiersIncomplets ?? '—',
            detail: 'dossiers à compléter',
            ton: 'text-amber-600',
          },
          {
            libelle: 'Admis en formation',
            valeur: effectif('ADMIS'),
            detail: 'prêts pour rentrée / placement',
            ton: 'text-codialis-mint',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Candidat / Apprenant' },
          { cle: 'formation', libelle: 'Formation visée' },
          { cle: 'progression', libelle: 'Dossier & pièces' },
          { cle: 'financeur', libelle: 'Antenne & Prise en charge' },
          { cle: 'statut', libelle: 'Statut candidature' },
        ],
        statutsPrioritaires: [
          'INSCRIT',
          'ENTRETIEN_PLANIFIE',
          'ENTRETIEN_REALISE',
          'ADMIS',
          'DOCUMENTS_EN_ATTENTE',
          'DOSSIER_INCOMPLET',
        ],
        bandeauBas: {
          titre: 'Constitution des dossiers de candidature',
          badgeTexte: (compteurs) =>
            `${compteurs?.entretiensAMener ?? 0} entretien${
              (compteurs?.entretiensAMener ?? 0) > 1 ? 's' : ''
            } à planifier / mener`,
          badgeTon: 'info',
          description:
            'Instruisez les candidatures, planifiez les entretiens de sélection et assurez la complétude des pièces justificatives avant validation définitive de l’admission.',
          boutonAction: {
            libelle: 'Voir les candidats à instruire',
            statutFiltre: 'INSCRIT,ENTRETIEN_PLANIFIE,DOCUMENTS_EN_ATTENTE',
          },
          liensSecondaires: [
            { to: '/com-entretiens', libelle: 'Planning des entretiens', mode: 'bouton' },
            { to: '/com-relances', libelle: 'Relances mail / SMS', mode: 'lien' },
          ],
        },
      };

    case 'RELATION_ENTREPRISE':
      return {
        profil: 'RELATION_ENTREPRISE',
        badgePole: 'Pôle Relation Entreprise',
        titre: 'Candidats & Placement Entreprise',
        sousTitre: ({ portefeuille, enFormation }) =>
          `${portefeuille} candidat${portefeuille > 1 ? 's' : ''} suivis · ${enFormation} en contrat ou formation`,
        actionsSecondaires: [
          { libelle: 'Matching entreprises', icone: 'cible', to: '/com-matching' },
          { libelle: 'Entreprises partenaires', icone: 'batiment', to: '/com-entreprises' },
        ],
        indicateurs: ({ portefeuille, effectif }) => [
          { libelle: 'Vivier à positionner', valeur: portefeuille, detail: 'candidats éligibles' },
          {
            libelle: 'En recherche entreprise',
            valeur: effectif('RECHERCHE_ENTREPRISE') + effectif('ADMIS'),
            detail: 'à placer en priorité',
            ton: 'text-amber-600',
          },
          {
            libelle: 'Contrats signés',
            valeur: effectif('CONTRAT_SIGNE'),
            detail: 'placements concrétisés',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'En formation',
            valeur: effectif('EN_FORMATION'),
            detail: 'parcours sécurisés',
          },
          {
            libelle: 'Ruptures à reclasser',
            valeur: effectif('RUPTURE'),
            detail: 'recherches urgentes',
            ton: 'text-red-500',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Candidat / Apprenant' },
          { cle: 'formation', libelle: 'Formation' },
          { cle: 'cv', libelle: 'CV' },
          { cle: 'statut', libelle: 'Statut placement' },
        ],
        statutsPrioritaires: [
          'ADMIS',
          'RECHERCHE_ENTREPRISE',
          'CONTRAT_SIGNE',
          'EN_FORMATION',
          'RUPTURE',
        ],
        bandeauBas: {
          titre: 'Placement et immersions en entreprise',
          badgeTexte: (_, effectifs) => {
            const count = (effectifs?.effectif('RUPTURE') ?? 0) + (effectifs?.effectif('ADMIS') ?? 0);
            return `${count} candidat${count > 1 ? 's' : ''} à positionner`;
          },
          badgeTon: 'warning',
          description:
            'Positionnez les candidats admis auprès des entreprises partenaires, suivez les immersions et concrétisez la signature des contrats d’alternance.',
          boutonAction: {
            libelle: 'Voir les candidats à placer',
            statutFiltre: 'ADMIS,RECHERCHE_ENTREPRISE,RUPTURE',
          },
          liensSecondaires: [
            { to: '/com-matching', libelle: 'Matching entreprises', mode: 'bouton' },
            { to: '/com-entreprises', libelle: 'Entreprises partenaires', mode: 'lien' },
          ],
        },
      };

    case 'PEDAGOGIQUE':
      return {
        profil: 'PEDAGOGIQUE',
        badgePole: 'Pôle Pédagogique',
        titre: 'Suivi Pédagogique des Apprenants',
        sousTitre: ({ portefeuille, enFormation }) =>
          `${portefeuille} apprenant${portefeuille > 1 ? 's' : ''} suivi${
            portefeuille > 1 ? 's' : ''
          } · ${enFormation} en formation active`,
        actionsSecondaires: [
          { libelle: 'Hub pédagogique', icone: 'livre', to: '/ped-hub' },
          { libelle: 'Sessions & promotions', icone: 'dossier', to: '/ped-sessions' },
        ],
        indicateurs: ({ portefeuille, compteurs, effectif }) => [
          { libelle: 'Apprenants suivis', valeur: portefeuille, detail: 'toutes promotions' },
          {
            libelle: 'En formation active',
            valeur: effectif('EN_FORMATION'),
            detail: 'parcours en cours',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'Contrats signés',
            valeur: effectif('CONTRAT_SIGNE'),
            detail: 'intégrations validées',
          },
          {
            libelle: 'Dossiers incomplets',
            valeur: compteurs?.dossiersIncomplets ?? '—',
            detail: 'conformité réglementaire',
            ton: 'text-amber-600',
          },
          {
            libelle: 'Ruptures de parcours',
            valeur: effectif('RUPTURE'),
            detail: 'signalements à traiter',
            ton: 'text-red-500',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Apprenant' },
          { cle: 'formation', libelle: 'Formation / Session' },
          { cle: 'progression', libelle: 'Complétude dossier' },
          { cle: 'financeur', libelle: 'Antenne & Prise en charge' },
          { cle: 'statut', libelle: 'Statut de parcours' },
        ],
        bandeauBas: {
          titre: 'Accompagnement pédagogique et assiduité',
          badgeTexte: (_, effectifs) => {
            const count = effectifs?.effectif('EN_FORMATION') ?? 0;
            return `${count} apprenant${count > 1 ? 's' : ''} en formation`;
          },
          badgeTon: 'mint',
          description:
            'Suivez l’assiduité, prévenez le décrochage pédagogique et supervisez l’avancement des évaluations ECF et des sessions de certification.',
          boutonAction: {
            libelle: 'Voir les apprenants en formation',
            statutFiltre: 'EN_FORMATION',
          },
          liensSecondaires: [
            { to: '/ped-hub', libelle: 'Hub pédagogique', mode: 'bouton' },
            { to: '/ped-sessions', libelle: 'Sessions & promotions', mode: 'lien' },
          ],
        },
      };

    case 'FORMATEUR':
      return {
        profil: 'FORMATEUR',
        badgePole: 'Espace Formateur',
        titre: 'Mes apprenants',
        sousTitre: ({ portefeuille, enFormation }) =>
          `${portefeuille} apprenant${portefeuille > 1 ? 's' : ''} rattaché${
            portefeuille > 1 ? 's' : ''
          } · ${enFormation} en cours de session`,
        actionsSecondaires: [
          { libelle: 'Mes promotions', icone: 'utilisateurs', to: '/instructor-promos' },
          { libelle: 'Émargement des séances', icone: 'stylo', to: '/emargement' },
        ],
        indicateurs: ({ portefeuille, compteurs, effectif }) => [
          { libelle: 'Mes apprenants', valeur: portefeuille, detail: 'inscrits sur mon périmètre' },
          {
            libelle: 'En formation',
            valeur: effectif('EN_FORMATION'),
            detail: 'sessions actives',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'Parcours terminés',
            valeur: effectif('TERMINE'),
            detail: 'cursus validés',
          },
          {
            libelle: 'Dossiers à consolider',
            valeur: compteurs?.dossiersIncomplets ?? '—',
            detail: 'suivi administratif',
            ton: 'text-amber-600',
          },
          {
            libelle: 'Alertes & ruptures',
            valeur: effectif('RUPTURE'),
            detail: 'interruptions signalées',
            ton: 'text-red-500',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Apprenant' },
          { cle: 'formation', libelle: 'Formation' },
          { cle: 'progression', libelle: 'Complétude dossier' },
          { cle: 'financeur', libelle: 'Antenne & Mode' },
          { cle: 'statut', libelle: 'Statut' },
        ],
        masquerFinanceurSensible: true,
        bandeauBas: {
          titre: 'Animation pédagogique et présences',
          badgeTexte: (_, effectifs) => {
            const count = effectifs?.effectif('EN_FORMATION') ?? 0;
            return `${count} apprenant${count > 1 ? 's' : ''} actifs`;
          },
          badgeTon: 'mint',
          description:
            'Consultez les dossiers de vos apprenants en lecture seule, adaptez vos cours selon les situations déclarées (RQTH, mineurs) et validez vos fiches séquences.',
          boutonAction: {
            libelle: 'Voir les apprenants actifs',
            statutFiltre: 'EN_FORMATION',
          },
          liensSecondaires: [
            { to: '/instructor-promos', libelle: 'Mes promotions', mode: 'bouton' },
            { to: '/emargement', libelle: 'Émargement', mode: 'lien' },
          ],
        },
      };

    case 'DIRECTION':
      return {
        profil: 'DIRECTION',
        badgePole: 'Direction',
        titre: 'Registre des Apprenants',
        sousTitre: ({ portefeuille, enFormation }) =>
          `Vue globale direction · ${portefeuille} dossier${
            portefeuille > 1 ? 's' : ''
          } consolidé${portefeuille > 1 ? 's' : ''} · ${enFormation} en formation active`,
        actionsSecondaires: [],
        indicateurs: ({ portefeuille, compteurs, effectif }) => [
          { libelle: 'Portefeuille consolidé', valeur: portefeuille, detail: 'toutes antennes & sessions' },
          {
            libelle: 'Inscriptions du mois',
            valeur: compteurs?.inscriptionsDuMois ?? '—',
            detail: 'dynamique globale',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'En formation active',
            valeur: effectif('EN_FORMATION'),
            detail: 'taux d’occupation',
            ton: 'text-codialis-navy',
          },
          {
            libelle: 'Conformité & incomplets',
            valeur: compteurs?.dossiersIncomplets ?? '—',
            detail: 'surveillance Qualiopi',
            ton: 'text-red-500',
          },
          {
            libelle: 'Ruptures globales',
            valeur: effectif('RUPTURE'),
            detail: 'vigilance CFA',
            ton: 'text-amber-600',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Apprenant' },
          { cle: 'formation', libelle: 'Formation' },
          { cle: 'progression', libelle: 'Progression' },
          { cle: 'financeur', libelle: 'Financeur' },
          { cle: 'statut', libelle: 'Statut' },
        ],
        bandeauBas: {
          titre: 'Pilotage centralisé et veille réglementaire',
          badgeTexte: (compteurs) =>
            `${compteurs?.dossiersIncomplets ?? 0} dossier${
              (compteurs?.dossiersIncomplets ?? 0) > 1 ? 's' : ''
            } incomplet${(compteurs?.dossiersIncomplets ?? 0) > 1 ? 's' : ''}`,
          badgeTon: 'danger',
          description:
            'Supervisez la conformité des dossiers Qualiopi, les alertes de rupture et la cadence de relances administratives sur l’ensemble des sites.',
          boutonAction: {
            libelle: 'Voir les dossiers incomplets',
            statutFiltre: 'DOCUMENTS_EN_ATTENTE,DOSSIER_INCOMPLET',
          },
          liensSecondaires: [
            { to: '/adm-export', libelle: 'Export consolidé', mode: 'bouton' },
            { to: '/alertes', libelle: 'Configurer les alertes', mode: 'lien' },
          ],
        },
      };

    // « Dossiers apprenants » du pôle facturation (TF-464) : la vue de
    // l'administratif, qui y renvoie au financement plutôt qu'aux relances.
    case 'FACTURATION': {
      const administratif = getLearnersViewConfig('ADMINISTRATIF');
      return {
        ...administratif,
        profil: 'FACTURATION',
        badgePole: 'Pôle Facturation',
        titre: 'Dossiers apprenants',
        bandeauBas: {
          ...administratif.bandeauBas,
          liensSecondaires: [
            { to: '/financing', libelle: 'Financeurs & OPCO', mode: 'bouton' },
            { to: '/alertes', libelle: 'Alertes du périmètre', mode: 'lien' },
          ],
        },
      };
    }

    case 'ADMINISTRATIF':
    default:
      return {
        profil: 'ADMINISTRATIF',
        badgePole: 'Pôle Administratif',
        titre: 'Apprenants',
        sousTitre: ({ portefeuille, enFormation }) =>
          `${portefeuille} dossier${portefeuille > 1 ? 's' : ''} suivi${
            portefeuille > 1 ? 's' : ''
          } · ${enFormation} en formation`,
        actionsSecondaires: [],
        indicateurs: ({ portefeuille, compteurs, effectif }) => [
          { libelle: 'Dossiers suivis', valeur: portefeuille, detail: 'toutes phases' },
          {
            libelle: 'Inscriptions',
            valeur: compteurs?.inscriptionsDuMois ?? '—',
            detail: 'depuis le 1er du mois',
            ton: 'text-codialis-teal',
          },
          {
            libelle: 'En formation',
            valeur: effectif('EN_FORMATION'),
            detail: 'parcours en cours',
          },
          {
            libelle: 'Dossiers incomplets',
            valeur: compteurs?.dossiersIncomplets ?? '—',
            detail: 'relance tous les 15 jours',
            ton: 'text-red-500',
          },
          {
            libelle: 'Ruptures',
            valeur: effectif('RUPTURE'),
            detail: 'à reclasser',
            ton: 'text-amber-600',
          },
        ],
        colonnes: [
          { cle: 'apprenant', libelle: 'Apprenant' },
          { cle: 'formation', libelle: 'Formation' },
          { cle: 'progression', libelle: 'Progression' },
          { cle: 'financeur', libelle: 'Financeur' },
          { cle: 'statut', libelle: 'Statut' },
        ],
        bandeauBas: {
          titre: 'Dossiers à relancer · suivi automatique',
          badgeTexte: (compteurs) =>
            `${compteurs?.dossiersIncomplets ?? 0} dossier${
              (compteurs?.dossiersIncomplets ?? 0) > 1 ? 's' : ''
            } incomplet${(compteurs?.dossiersIncomplets ?? 0) > 1 ? 's' : ''}`,
          badgeTon: 'danger',
          description:
            'Les dossiers dont les pièces manquent sont relancés tous les 15 jours, et le moteur d’alertes signale les échéances : fin de période d’essai à 45 jours, seuil des 5 mois, examen à 1 mois de la fin de contrat.',
          boutonAction: {
            libelle: 'Voir les dossiers incomplets',
            statutFiltre: 'DOCUMENTS_EN_ATTENTE,DOSSIER_INCOMPLET',
          },
          liensSecondaires: [
            { to: '/com-relances', libelle: 'Relances mail / SMS', mode: 'bouton' },
            { to: '/alertes', libelle: 'Configurer les alertes', mode: 'lien' },
          ],
        },
      };
  }
}
